import { ClientShortHandName } from '@snoam/mono-scc';
import { getIdentity } from './actions/Actions';
import {
  IAddress,
  IProductInfo,
  IReferer,
  IUserInfo,
  ProductId,
} from './CustomTypes';
import StorageService from './services/StorageService';
import { ILoginOptions } from '@schibsted/account-sdk-browser';
import moment from 'moment';

/**
 * Create a number array from and including start/stop
 */
export function createNumberArray(start: number, stop: number): number[] {
  return Array(stop - start + 1)
    .fill(0)
    .map((_, i) => start + i);
}

/**
 * Create a date string on the format 2016-12-31
 */
export function createDateString(
  day: number | string,
  month: number | string,
  year: number | string,
) {
  if (!day || !month || !year) {
    return '';
  }
  return `${year}-${padWithZerosLengthTwo(month)}-${padWithZerosLengthTwo(
    day,
  )}`;
}

export function padWithZerosLengthTwo(num: number | string) {
  return ('00' + num).substr(-2, 2);
}

export function padWithZerosLengthFour(num: number | string) {
  return ('0000' + num).substr(-4, 4);
}

function getRelativeUrl() {
  return location.href.substring(0, location.href.indexOf('/products/'));
}

export function getUrlForPage(productId: ProductId, page: string) {
  return `${getRelativeUrl()}/products/${productId}/${page}`;
}

export function createAddStrexUrl(
  owningClient: ClientShortHandName | '',
  productId: ProductId,
  spidId: number,
  email: string,
) {
  return `/bestilling/c/${owningClient}/api/v2/ff/addPaymentPhone.json?productId=${productId}&spidId=${spidId}&email=${encodeURIComponent(
    email,
  )}`;
}

export function createStartPageFullUrl(productId: ProductId) {
  return getUrlForPage(productId, '');
}

export function isValidUntilPassed(validUntil?: number) {
  return validUntil && validUntil < Date.now();
}

let lastId = 0;

export function newId() {
  return ++lastId;
}

export function encode(object: object) {
  return window.btoa(JSON.stringify(object));
}

export function decode(object: string): any {
  return JSON.parse(window.atob(object));
}

export function createB64data(
  address: IAddress,
  referer: IReferer,
  phoneNumber: string,
  userCreated: boolean,
  email: string,
): string {
  const obj: any = {};
  if (address && (address.zipCode || address.zipCode)) {
    obj.address = address;
  }
  if (referer && (referer.referer || referer.redirectUri)) {
    obj.referer = referer;
  }
  if (phoneNumber) {
    obj.phoneNumber = phoneNumber;
  }
  if (userCreated) {
    obj.userCreated = userCreated;
  }
  if (email) {
    obj.email = email;
  }
  return encode(obj);
}

export function urlEncodeObject(object: { [index: string]: string }) {
  const urlEncodedObject: { [index: string]: string } = {};
  Object.keys(object).forEach((it) => {
    urlEncodedObject[it] = encodeURIComponent(object[it]);
  });
  return urlEncodedObject;
}

export function isProduction(
  location: Pick<Location, 'hostname'> = window.location,
) {
  return !/localhost|test|\.lh$/.test(location.hostname);
}

export function getSchibstedServerName() {
  return isProduction() ? 'payment.schibsted.no' : 'identity-pre.schibsted.com';
}

export function getSchibstedEnvName(
  location: Pick<Location, 'hostname'> = window.location,
) {
  return isProduction(location) ? 'PRO_NO' : 'PRE';
}

export function doSpidLoginByEmailCode(
  owningClient: ClientShortHandName | '',
  productId: ProductId,
  email: string,
  teaser: string,
) {
  return getIdentity(owningClient).login({
    acrValues: 'otp-email',
    loginHint: email,
    redirectUri: `https://${isProduction() ? '' : 'test-'}redirect.s-plat.no`,
    state: JSON.stringify({
      redirectUri: `${getUrlForPage(
        productId,
        'start',
      )}?${getRedirectUrlInLocalStorage()}`,
    }),
    teaser,
  } as ILoginOptions);
}

export function doSpidLoginLinkWithRedirectToStart(
  owningClient: ClientShortHandName | '',
  productId: ProductId,
  email: string,
  teaser: string,
  base64DataDecoded?: object,
) {
  return getIdentity(owningClient).login({
    loginHint: email,
    redirectUri: `https://${isProduction() ? '' : 'test-'}redirect.s-plat.no`,
    state: JSON.stringify({
      redirectUri: `${getUrlForPage(
        productId,
        'start',
      )}?${getRedirectUrlInLocalStorage(base64DataDecoded)}`,
    }),
    teaser,
  } as ILoginOptions);
}

export function createLoginSpidLoginLinkWithRedirectUrl(
  owningClient: ClientShortHandName | '',
  email: string,
  redirectUrl: string,
  spidTeaser: string,
) {
  const redirectState = JSON.stringify({
    redirectUri: `${redirectUrl}`,
  });
  return getIdentity(owningClient).loginUrl(
    redirectState,
    undefined,
    'openid',
    `https://${isProduction() ? '' : 'test-'}redirect.s-plat.no`,
    true,
    undefined,
    undefined,
    spidTeaser,
  );
}

export function toTitleCase(str?: string) {
  if (!str) {
    return '';
  }
  return str.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}
/*
export function triggerHotjar(trigger: string, eventName: string) {
  if (window && (window as any).hj) {
    (window as any).hj(trigger, eventName);
  }
}
*/

export interface ILocalStorageData {
  utmCampaign: string;
  utmContent: string;
  utmMedium: string;
  utmSource: string;
  utmTerm: string;
  referer: string;
  redirectUri: string;
}

const storageService = new StorageService();

export function setLoyaltyCode(code: string) {
  storageService.setItem('ff_loyaltyCode', code);
}

export function getLoyaltyCode(): string | undefined {
  try {
    const loyaltyCode = storageService.getItem('ff_loyaltyCode');
    if (loyaltyCode) {
      return loyaltyCode;
    }
  } catch (e) {
    // ignored
  }
}

export function setStorageData(data: ILocalStorageData) {
  storageService.setItem('ff_url_data', JSON.stringify(data));
}

export function getStorageData(): ILocalStorageData | null {
  try {
    const localStorageData: Partial<ILocalStorageData> = JSON.parse(
      storageService.getItem('ff_url_data'),
    );
    if (localStorageData) {
      return {
        redirectUri: localStorageData.redirectUri || '',
        referer: localStorageData.referer || '',
        utmCampaign: localStorageData.utmCampaign || '',
        utmContent: localStorageData.utmContent || '',
        utmMedium: localStorageData.utmMedium || '',
        utmSource: localStorageData.utmSource || '',
        utmTerm: localStorageData.utmTerm || '',
      };
    }
  } catch (e) {
    // ignored
  }
  return null;
}

export function getRefererFromLocalStorage() {
  const storageData = getStorageData();
  const referer =
    (storageData && storageData.referer && storageData.referer) || '';

  const [source, channel, content, campaign] = referer.split('_');

  return {
    campaign: campaign || '',
    channel: channel || '',
    content: content || '',
    source: source || '',
  };
}

/**
 * Generate redirect URL based on data in localStorage. Will fall back to fetching referer from
 * urlBase64DataDecodedFallback if it isn't set in localStorage.
 * @param urlBase64DataDecodedFallback object of decoded base64 data from the URL.
 */
export function getRedirectUrlInLocalStorage(
  urlBase64DataDecodedFallback?: any,
) {
  const b64data = urlBase64DataDecodedFallback;
  const localStorageData: ILocalStorageData = getStorageData();
  const referer =
    (localStorageData && localStorageData.referer) ||
    (b64data && b64data.referer && b64data.referer.referer);
  if (localStorageData) {
    /* tslint:disable-line:max-line-length */
    const utmTags = `utm_campaign=${encodeURIComponent(
      localStorageData.utmCampaign,
    )}&utm_content=${encodeURIComponent(
      localStorageData.utmContent,
    )}&utm_medium=${encodeURIComponent(
      localStorageData.utmMedium,
    )}&utm_source=${encodeURIComponent(
      localStorageData.utmSource,
    )}&utm_term=${encodeURIComponent(localStorageData.utmTerm)}`;

    if (
      localStorageData.redirectUri ||
      localStorageData.redirectUri !== 'undefined'
    ) {
      return `redirect_uri=${encodeURIComponent(
        localStorageData.redirectUri,
      )}&referer=${encodeURIComponent(referer)}&${utmTags}`;
    } else {
      return `referer=${encodeURIComponent(referer)}&${utmTags}`;
    }
  } else {
    return null;
  }
}

export function safeRedirect(redirectUri: string): string {
  if (!redirectUri || redirectUri === 'null' || redirectUri === 'undefined') {
    return null;
  }
  if (/^https:\/\/(test-)?redirect\.s-plat\.no\?state=.*$/.test(redirectUri)) {
    return redirectUri;
  }
  return `https://${
    isProduction() ? '' : 'test-'
  }redirect.s-plat.no?state=${encodeURIComponent(
    JSON.stringify({ redirectUri }),
  )}`;
}

export function shouldSkipSelectPayment(productInfo: IProductInfo): boolean {
  if (productInfo === undefined) {
    return false;
  }
  const { paymentMethodFree, price } = productInfo;
  return paymentMethodFree && (price === undefined || price === 0);
}

export const getBasename = () => {
  if (
    window.location.hostname.includes('localhost') ||
    window.location.hostname.includes('.lh')
  ) {
    return '/';
  } else {
    return '/s/ff/';
  }
};

export const trimmedBasename = () => {
  const baseName = getBasename();

  //  if not on localhost
  if (baseName !== '/') {
    const baseNameTrimLeadingSlash =
      baseName.charAt(0) === '/' && baseName.substr(1);

    if (
      baseNameTrimLeadingSlash.charAt(baseNameTrimLeadingSlash.length - 1) ===
      '/'
    ) {
      return baseNameTrimLeadingSlash.substr(
        0,
        baseNameTrimLeadingSlash.length - 1,
      );
    }
  }

  return '';
};

export function getCoreHeaderStyleFromClientName(clientName: string) {
  if (clientName.toLowerCase() === 'bti') {
    return 'bt';
  }
  if (clientName.toLowerCase() === 'byn') {
    return 'bnytt';
  }
  if (clientName.toLowerCase() === 'dp') {
    return 'e24';
  }
  if (clientName.toLowerCase() === 'vny') {
    return 'vnytt';
  }
  if (clientName.toLowerCase() === 'ask') {
    return 'avavis';
  }
  if (clientName.toLowerCase() === 'fak') {
    return 'far';
  }
  if (clientName.toLowerCase() === 'lin') {
    return 'lind';
  }
  if (clientName.toLowerCase() === 'str') {
    return 'stri';
  }
  return clientName;
}

export const isHermes = (userAgent) => /hermes/i.test(userAgent);
export const isIOS = (userAgent) =>
  /(i[PSa-z\s]+);.*?CPU\s([OSPa-z\s]+(?:([\d_]+)|;))/i.test(userAgent);
export const isAndroid = (userAgent) => /android/gi.test(userAgent);

export function rgb2hex(rgb) {
  rgb = rgb.match(
    /^rgb?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i,
  );
  return rgb && rgb.length === 4
    ? '#' +
        ('0' + parseInt(rgb[1], 10).toString(16)).slice(-2) +
        ('0' + parseInt(rgb[2], 10).toString(16)).slice(-2) +
        ('0' + parseInt(rgb[3], 10).toString(16)).slice(-2)
    : '';
}

export const forceE24Login = (
  owningClient: ClientShortHandName | '',
  redirectUri: string,
) => {
  if (owningClient !== '' && owningClient !== ClientShortHandName.E24) {
    return redirectUri ? redirectUri.includes('e24.no') : false;
  }
  return false;
};

const kundeportalDomains: { [key in ClientShortHandName]: string } = {
  [ClientShortHandName.AB]: 'ignored',
  [ClientShortHandName.BAREASANE]: 'ignored',
  [ClientShortHandName.OMNI]: 'ignored',
  [ClientShortHandName.OP]: 'ignored',
  [ClientShortHandName.RB24]: 'ignored',
  [ClientShortHandName.SVD]: 'ignored',
  [ClientShortHandName.VEKTKLUBB]: 'ignored',
  [ClientShortHandName.WELLOBE]: 'ignored',
  [ClientShortHandName.AP]: 'aftenposten.no',
  [ClientShortHandName.ASK]: 'av-avi.no',
  [ClientShortHandName.BTI]: 'bt.no',
  [ClientShortHandName.BEDRIFT]: 'aftenposten.no',
  [ClientShortHandName.BYN]: 'bygdanytt.no',
  [ClientShortHandName.DNB]: 'aftenposten.no',
  [ClientShortHandName.DP]: 'e24dinepenger.no',
  [ClientShortHandName.E24]: 'e24dinepenger.no',
  [ClientShortHandName.MPL]: 'magasinpluss.no',
  [ClientShortHandName.SA]: 'aftenbladet.no',
  [ClientShortHandName.SCH]: 'schibsted.no',
  [ClientShortHandName.STR]: 'strilen.no',
  [ClientShortHandName.VG]: 'vg.no',
  [ClientShortHandName.VNY]: 'vestnytt.no',
};

export const kundeportalDomainFromClientShortHandName = (
  clientShortHandName: ClientShortHandName,
) => {
  return kundeportalDomains[clientShortHandName];
};

// Use Siebel birthdate if the siebel user exists, else use the spid birthdate and return the moment
// If the siebel user exists without a bithdate we return null - see SNOAM-13874 for business rules
export function getBirthdateMoment(user: IUserInfo) {
  const { birthDay, siebelBirthDate, siebelUserExists } = user;
  if (siebelUserExists && siebelBirthDate) {
    return moment(siebelBirthDate, 'MM/DD/YYYY');
  }
  if (!siebelUserExists) {
    return moment(birthDay, 'YYYY-MM-DD');
  }
  return null;
}

// If onlyForCertainAge checkbox is not selected and a different product (ageLimitSubscriptionConfigurationId)
// is supplied and the user is under the required age return true
export function alternativeOfferForUnderAgeUsers(
  user: IUserInfo,
  product: IProductInfo,
) {
  const birthdateMoment = getBirthdateMoment(user);
  const { ageLimit } = product;
  if (
    birthdateMoment &&
    birthdateMoment.isValid() &&
    !product.onlyForCertainAge &&
    product.ageLimitSubscriptionConfigurationId &&
    product.ageLimit
  ) {
    const toDay = moment();
    return toDay.isBefore(birthdateMoment.add(ageLimit, 'years'));
  }
  return false;
}

// If onlyForCertainAge checkbox is selected to lock this offer for a certain age and
// the user is over the required age return true; If no birthdate is found return also true
export function lockedForOverAgeUsers(user: IUserInfo, product: IProductInfo) {
  const birthdateMoment = getBirthdateMoment(user);
  const { ageLimit } = product;

  if (product.onlyForCertainAge && product.ageLimit) {
    if (birthdateMoment && birthdateMoment.isValid()) {
      const toDay = moment();
      return toDay.isAfter(birthdateMoment.add(ageLimit, 'years'));
    }
    if (!birthdateMoment) {
      return true;
    }
  }

  return false;
}

export const lowerAgeLimitYears = 18;

export const getRegretFormLink = (owningClient: ClientShortHandName) => {
  switch (owningClient) {
    case ClientShortHandName.AP:
    case ClientShortHandName.MPL:
      return 'https://snotech-media.s3.amazonaws.com/CRM/Angrerettskjema/Angrerettskjema_2023_Aftenposten.pdf';
    case ClientShortHandName.BTI:
      return 'https://snotech-media.s3.amazonaws.com/CRM/Angrerettskjema/Angrerettskjema_2023_Bergens_Tidende.pdf';
    case ClientShortHandName.SA:
      return 'https://snotech-media.s3.amazonaws.com/CRM/Angrerettskjema/Angrerettskjema_2023_Stavanger_Aftenblad.pdf';
    case ClientShortHandName.ASK:
      return 'https://snotech-media.s3.amazonaws.com/CRM/Angrerettskjema/Angrerettskjema_2023_Ask%C3%B8yv%C3%A6ringen.pdf';
    case ClientShortHandName.BYN:
      return 'https://snotech-media.s3.amazonaws.com/CRM/Angrerettskjema/Angrerettskjema_2023_Bygdanytt.pdf';
    case ClientShortHandName.VNY:
      return 'https://snotech-media.s3.amazonaws.com/CRM/Angrerettskjema/Angrerettskjema_2023_Vestnytt.pdf';
    case ClientShortHandName.STR:
      return 'https://snotech-media.s3.amazonaws.com/CRM/Angrerettskjema/Angrerettskjema_2023_Strilen.pdf';
    case ClientShortHandName.VG:
    case ClientShortHandName.E24:
    case ClientShortHandName.DP:
      return 'https://snotech-media.s3.amazonaws.com/CRM/Angrerettskjema/Angrerettskjema_2023_VG.pdf';
    default:
      return '';
  }
};

export const getCompanyAddress = (owningClient: ClientShortHandName) => {
  switch (owningClient) {
    case ClientShortHandName.AP:
    case ClientShortHandName.MPL:
      return `Aftenposten AS\nAkersgata 55\n0180 Oslo`;
    case ClientShortHandName.BTI:
      return `Bergens Tidende AS\nLars Hilles gate 30 / Odd Frantzens plass 5\n5008 Bergen`;
    case ClientShortHandName.SA:
      return `Stavanger Aftenblad AS\nPostboks 229\n4001 Stavanger`;
    case ClientShortHandName.ASK:
      return `Askøyværingen AS\nBildøyvegen 11C\n5353 Straume`;
    case ClientShortHandName.BYN:
      return `Bygdanytt AS\nØyrane Torg\nÅdnavegen 63\n5260 Indre Arna`;
    case ClientShortHandName.VNY:
      return `Vestnytt AS\nBildøyvegen 11C\n5353 Straume`;
    case ClientShortHandName.STR:
      return `Strilen AS\nHagellia 4\n5914 Isdalstø`;
    case ClientShortHandName.VG:
    case ClientShortHandName.E24:
    case ClientShortHandName.DP:
      return `Verdens Gang AS\nAkersgata 55\n0180 Oslo`;
    default:
      return '';
  }
};

export const getCompanyContactInfo = (owningClient: ClientShortHandName) => {
  switch (owningClient) {
    case ClientShortHandName.AP:
      return `Tlf: 22863000\nE-post: abonnement@aftenposten.no`;
    case ClientShortHandName.MPL:
      return `Tlf: 22863000\nE-post: magasin@aftenposten.no`;
    case ClientShortHandName.BTI:
      return `Tlf: 56913740\nE-post: bt@bt.no`;
    case ClientShortHandName.SA:
      return `Tlf: 52900810\nE-post: abonnement@aftenbladet.no`;
    case ClientShortHandName.ASK:
      return `Tlf: 56913750\nE-post: abonnement@av-avis.no`;
    case ClientShortHandName.BYN:
      return `Tlf: 56913760\nE-post: abonnent@bygdanytt.no`;
    case ClientShortHandName.VNY:
      return `Tlf: 56913780\nE-post: abo@vestnytt.no`;
    case ClientShortHandName.STR:
      return `Tlf: 56913770\nE-post: abonnement@strilen.no`;
    case ClientShortHandName.VG:
    case ClientShortHandName.E24:
    case ClientShortHandName.DP:
      return `Tlf: 21951420\nE-post: support@vg.no`;
    default:
      return '';
  }
};
